
import _ from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'

import { withLegacyRouter } from '../../../../../decorators'
import { mapCondition, unmapCondition } from '../../../../../lib/Proxy/condition'
import { mapPatientToForm } from '../../../../../lib/Proxy/patient'
import { DiagnosesClaimsTable } from '../../../../claim-history/diagnoses-table'
import { IGAO } from '../../../../igao'
import * as Card from '../../../../shared/card'
import CrudTable from '../../../../shared/CrudTable'
import * as Actions from '../../../../shared/CrudTable/actions'
import ConfirmationDeleteDialog from '../../../../shared/DeleteDialog/ConfirmationDeleteDialog'

import { diagnosisIssueCheck, getDiagnoses } from './actions'
import DiagnosisForm from './components/form'
import { HealthProgram } from './healthProgram'

const mkSpec = patientId => ({
  type: 'diagnosis',
  form: DiagnosisForm,
  urls: {
    get: () => `/patients/${patientId}/diagnoses`,
    create: () => `/patients/${patientId}/diagnoses`,
    update: entry => `/patients/${patientId}/diagnoses/${entry.id}`,
    delete: entry => `/patients/${patientId}/diagnoses/${entry.id}`
  },
  mapFns: {
    toForm: unmapCondition,
    toFhir: entry => mapCondition(patientId, entry)
  },
  table: [
    {
      title: 'Diagnosis',
      displayFn: ({ name }) => {
        if (name) {
          return name.display
        }
        return null
      }
    },
    {
      title: 'Code',
      displayFn: x => x.name?.code ?? '–'
    },
    {
      title: 'Chapter',
      displayFn: x => x.chapter
    },
    {
      title: 'Category',
      displayFn: x => x.category
    },
    {
      title: 'Treating Physician',
      displayFn: x => x.treatingPhysician
    },
    {
      title: 'Severity',
      displayFn: x => x.severity
    },
    {
      title: 'Onset Month',
      displayFn: x => x.onsetMonth
    },
    {
      title: 'Onset Year',
      displayFn: x => x.onsetYear
    }
  ]
})

class DiagnosesGrid extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      key: 0,
      triggeredIssues: false,
      parOpen: true,
      diagnosisOpen: true
    }

    this.handleClaimsTableAction = this.handleClaimsTableAction.bind(this)
    this.handleClaimsTableMount = this.handleClaimsTableMount.bind(this)
  }

  componentWillUnmount () {
    this.triggerIssues()
  }

  // queue lack of entry error
  triggerIssues () {
    const { dispatch, diagnoses } = this.props

    // No Diagnoses Documented
    if (diagnoses && diagnoses.length === 0 && !this.triggeredIssues) {
      dispatch(diagnosisIssueCheck())
      this.triggeredIssues = true
    } else if (diagnoses && diagnoses.length && this.state.triggeredIssues) {
      dispatch(diagnosisIssueCheck())
      this.triggeredIssues = true
    }
  }

  handleClaimsTableAction ({ spec }) {
    return () => {
      this.props.dispatch(getDiagnoses(this.props.params.id, 'Diagnosis'))
      this.setState(state => ({ key: state.key + 1 }))
      this.props.dispatch(Actions.getEntries(
        spec.type,
        spec.urls,
        spec.mapFns
      ))
    }
  }

  handleClaimsTableMount (refetch) {
    this.refetch = refetch
  }

  render () {
    const {
      patientResource,
      episodeOfCareResource,
      patientId,
      dispatch
    } = this.props
    const unmappedPatient = mapPatientToForm(patientResource)
    const spec = mkSpec(patientId)

    return (
      <div className='main'>
        <Card.Card expandable>
          <Card.Header>
            <h2>Patient Acuity Rating</h2>
          </Card.Header>
          <Card.ExpandableWrapper
            style={{ padding: '0 1rem 1rem 1rem' }}
          >
            <HealthProgram
              formKey='patientAcuityRating'
              value={{
                primaryConcern: unmappedPatient.primaryConcern,
                healthProgram: unmappedPatient.healthProgram,
                par: unmappedPatient.par
              }}
              patientStatus={episodeOfCareResource?.status}
              expired={
                _.get(episodeOfCareResource, 'extension[0].valueString') ===
              'expired'
              }
              dispatch={dispatch}
              patientId={patientId}
              patient={unmappedPatient}
            />
          </Card.ExpandableWrapper>
        </Card.Card>

        <Card.Card expandable>
          <Card.Header>
            <h2>Diagnoses</h2>
          </Card.Header>
          <Card.ExpandableWrapper
            style={{ padding: '0 1rem 1rem 1rem' }}
          >
            <div className='row'>
              <div className='col-xs-12'>
                <CrudTable
                  labelButton='Diagnosis'
                  labelNoEntries='Diagnoses'
                  urlFns={spec.urls}
                  entryType={spec.type}
                  entrySet={patientId}
                  tableDefinition={spec.table}
                  mapFns={spec.mapFns}
                  form={spec.form}
                  deleteDialog={ConfirmationDeleteDialog}
                  callbackFn={() => {
                    this.props.dispatch(getDiagnoses(this.props.params.id, 'Diagnosis'))
                    this.setState(state => ({ key: state.key + 1 }))
                    this.refetch?.()
                  }}
                />
              </div>
            </div>
          </Card.ExpandableWrapper>
        </Card.Card>

        <DiagnosesClaimsTable
          onAction={this.handleClaimsTableAction({ spec })}
          onMount={this.handleClaimsTableMount}
        />

        <IGAO
          key={this.state.key}
          path='diagnosis'
        />
      </div>
    )
  }
}

DiagnosesGrid.contextTypes = {
  dispatch: PropTypes.instanceOf(Object)
}

const mapStateToProps = redux => {
  return {
    patientId: redux?.globalVars?.params?.id,
    encounterId: redux.encounter.id,
    diagnoses:
      redux.crudTable &&
      redux.crudTable.diagnoses &&
      redux.crudTable.diagnoses.entries,
    patientResource: Object.values(
      _.get(redux, 'patient.compartment.Patient', {})
    )[0],
    episodeOfCareResource: Object.values(
      _.get(redux, 'patient.compartment.EpisodeOfCare', {})
    )[0]
  }
}

const mapDispatchToProps = dispatch => ({
  dispatch
})

export default connect(mapStateToProps, mapDispatchToProps)(withLegacyRouter(DiagnosesGrid))
